/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { wgEngageAPI } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare, Vote, BookOpen, ListTodo, Activity, HeartPulse,
  Plus, Send, CheckCircle, XCircle, Clock, ChevronRight, Loader2,
  User, ShieldCheck, Eye, BarChart3, ArrowRight, Paperclip, FileText, Image, Download, Trash2,
} from 'lucide-react';

const DISC_STATUS = {
  acik: { label: 'Acik', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  tartisiliyor: { label: 'Tartisiliyor', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  oylamada: { label: 'Oylamada', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  karara_baglandi: { label: 'Karara Baglandi', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const DEC_STATUS = {
  bekliyor: { label: 'Bekliyor', cls: 'bg-amber-50 text-amber-700' },
  uygulaniyor: { label: 'Uygulaniyor', cls: 'bg-blue-50 text-blue-700' },
  tamamlandi: { label: 'Tamamlandi', cls: 'bg-emerald-50 text-emerald-700' },
};
const TASK_STATUS = {
  yapilacak: { label: 'Yapilacak', cls: 'bg-slate-100 text-slate-700' },
  yapiliyor: { label: 'Yapiliyor', cls: 'bg-blue-50 text-blue-700' },
  tamamlandi: { label: 'Tamamlandi', cls: 'bg-emerald-50 text-emerald-700' },
};
const ROLE_MAP = {
  uye: { label: 'Uye', cls: 'bg-slate-100 text-slate-600' },
  moderator: { label: 'Moderator', cls: 'bg-indigo-50 text-indigo-700' },
  rapurtor: { label: 'Raportor', cls: 'bg-teal-50 text-teal-700' },
  takipci: { label: 'Takipci', cls: 'bg-amber-50 text-amber-700' },
  karsilayici: { label: 'Karsilayici', cls: 'bg-rose-50 text-rose-700' },
};

// ===================== FAZ 1: TARTISMA PANOSU (Akis Bazli) =====================

// Adim gosterge bileşeni
const DiscussionFlowStepper = ({ status }) => {
  const steps = [
    { key: 'acik', label: 'Acik', icon: MessageSquare },
    { key: 'tartisiliyor', label: 'Tartisiliyor', icon: MessageSquare },
    { key: 'oylamada', label: 'Oylamada', icon: Vote },
    { key: 'karara_baglandi', label: 'Karara Baglandi', icon: BookOpen },
  ];
  const currentIdx = steps.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-1 w-full" data-testid="discussion-flow-stepper">
      {steps.map((step, idx) => {
        const StepIcon = step.icon;
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={step.key}>
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              isDone ? 'bg-emerald-100 text-emerald-700' : isCurrent ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'bg-slate-50 text-slate-400'
            }`}>
              {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : <StepIcon className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 min-w-[16px] rounded ${idx < currentIdx ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const DiscussionsTab = ({ organId, isAdmin, searchTerm = '', statusFilter = 'all' }) => {
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '' });
  const [creating, setCreating] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);
  // Oylama baslat dialog
  const [showVoteDialog, setShowVoteDialog] = useState(false);
  const [voteForm, setVoteForm] = useState({ title: '', description: '', vote_type: 'evet_hayir', options: '', deadline: '' });
  const [startingVote, setStartingVote] = useState(false);
  // Karar dialog
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decForm, setDecForm] = useState({ title: '', description: '', outcome: 'kabul', impact: '' });
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => { load(); }, [organId]);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await wgEngageAPI.getDiscussions(organId); setDiscussions(r.data); }
    catch { toast.error('Tartismalar yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const loadDetail = useCallback(async (discId) => {
    try { const r = await wgEngageAPI.getDiscussion(organId, discId); setDetail(r.data); setSelected(discId); }
    catch { toast.error('Tartisma yuklenemedi'); }
  }, []);

  const DISC_ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx', '.xls', '.xlsx'];
  const DISC_MAX_FILES = 4;
  const DISC_MAX_TOTAL_SIZE = 25 * 1024 * 1024;

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const allFiles = [...selectedFiles, ...newFiles];
    if (allFiles.length > DISC_MAX_FILES) { toast.error(`En fazla ${DISC_MAX_FILES} dosya ekleyebilirsiniz`); return; }
    const totalSize = allFiles.reduce((s, f) => s + f.size, 0);
    if (totalSize > DISC_MAX_TOTAL_SIZE) { toast.error('Toplam dosya boyutu 25MB\'yi asamaz'); return; }
    for (const f of newFiles) {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!DISC_ALLOWED_EXT.includes(ext)) { toast.error(`Desteklenmeyen dosya turu: ${ext}`); return; }
    }
    setSelectedFiles(allFiles);
    e.target.value = '';
  };

  const removeFile = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Baslik ve icerik zorunlu'); return; }
    setCreating(true);
    try {
      const res = await wgEngageAPI.createDiscussion(organId, form);
      const discId = res.data.id;
      // Upload files if any
      if (selectedFiles.length > 0) {
        const fd = new FormData();
        selectedFiles.forEach(f => fd.append('files', f));
        await wgEngageAPI.uploadDiscussionFiles(organId, discId, fd);
      }
      toast.success('Tartisma olusturuldu');
      setShowForm(false);
      setForm({ title: '', content: '' });
      setSelectedFiles([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    finally { setCreating(false); }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setCommenting(true);
    try { await wgEngageAPI.addComment(organId, selected, { content: commentText }); setCommentText(''); loadDetail(selected); }
    catch (e) { toast.error(e.response?.data?.detail || 'Yorum eklenemedi'); }
    finally { setCommenting(false); }
  };

  const handleStatusChange = async (discId, status) => {
    try { await wgEngageAPI.updateDiscussionStatus(organId, discId, status); toast.success('Durum guncellendi'); load(); if (selected === discId) loadDetail(discId); }
    catch (e) { toast.error(e.response?.data?.detail || 'Guncellenemedi'); }
  };

  // Oylama baslat
  const openVoteDialog = () => {
    setVoteForm({
      title: detail ? `${detail.title} - Oylama` : '',
      description: detail ? detail.content?.slice(0, 200) : '',
      vote_type: 'evet_hayir', options: '', deadline: ''
    });
    setShowVoteDialog(true);
  };

  const handleStartVote = async () => {
    if (!voteForm.title.trim()) { toast.error('Oylama basligi zorunlu'); return; }
    setStartingVote(true);
    try {
      const payload = { ...voteForm };
      if (payload.vote_type === 'coktan_secmeli') {
        payload.options = payload.options.split(',').map(s => s.trim()).filter(Boolean);
        if (payload.options.length < 2) { toast.error('En az 2 secenek giriniz'); setStartingVote(false); return; }
      } else {
        delete payload.options;
      }
      if (!payload.deadline) delete payload.deadline;
      await wgEngageAPI.startVoteFromDiscussion(organId, selected, payload);
      toast.success('Oylama baslatildi');
      setShowVoteDialog(false);
      loadDetail(selected);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Oylama baslatilamadi'); }
    finally { setStartingVote(false); }
  };

  // Oy kullan (inline)
  const handleCastVote = async (voteId, option) => {
    try { await wgEngageAPI.castVote(organId, voteId, { option }); toast.success('Oyunuz kaydedildi'); loadDetail(selected); }
    catch (e) { toast.error(e.response?.data?.detail || 'Oy kullanilamadi'); }
  };

  // Oylamayi kapat
  const handleCloseVote = async (voteId) => {
    try { await wgEngageAPI.closeVote(organId, voteId); toast.success('Oylama kapatildi'); loadDetail(selected); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kapatilamadi'); }
  };

  // Karara bagla
  const openDecisionDialog = () => {
    setDecForm({
      title: detail ? `${detail.title} - Karar` : '',
      description: '', outcome: 'kabul', impact: ''
    });
    setShowDecisionDialog(true);
  };

  const handleFinalize = async () => {
    if (!decForm.title.trim()) { toast.error('Karar basligi zorunlu'); return; }
    setFinalizing(true);
    try {
      const payload = { ...decForm };
      if (!payload.impact) delete payload.impact;
      await wgEngageAPI.finalizeDiscussion(organId, selected, payload);
      toast.success('Tartisma karara baglandi');
      setShowDecisionDialog(false);
      loadDetail(selected);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Karara baglanamadi'); }
    finally { setFinalizing(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  // ============ TARTISMA DETAY GORUNUMU ============
  if (selected && detail) {
    const st = DISC_STATUS[detail.status] || DISC_STATUS.acik;
    const linkedVote = detail.linked_vote;
    const linkedDec = detail.linked_decision;
    const voteActive = linkedVote?.status === 'aktif';
    const voteClosed = linkedVote?.status === 'kapandi';
    const totalVotes = linkedVote?.total_votes || 0;

    return (
      <div className="space-y-4" data-testid="discussion-detail">
        <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setDetail(null); }} data-testid="disc-back-btn">
          <XCircle className="w-4 h-4 mr-1" /> Geri
        </Button>

        {/* Akis Gosterge */}
        <DiscussionFlowStepper status={detail.status} />

        {/* Tartisma Icerigi */}
        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{detail.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{detail.created_by_name} - {formatRelativeTime(detail.created_at)}</p>
              </div>
              <Badge className={`${st.cls} border`}>{st.label}</Badge>
            </div>
            <p className="text-slate-700 whitespace-pre-wrap">{detail.content}</p>

            {/* Ekli Dosyalar */}
            {detail.files && detail.files.length > 0 && (
              <div className="mt-4 pt-3 border-t" data-testid="disc-files-section">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" /> Ekler ({detail.files.length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {detail.files.map(f => {
                    const isImg = /\.(jpg|jpeg|png)$/i.test(f.original_name);
                    const downloadUrl = wgEngageAPI.getDiscussionFileUrl(organId, detail.id, f.id);
                    return (
                      <a key={f.id} href={downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-colors group"
                        data-testid={`disc-file-dl-${f.id}`}>
                        {isImg ? <Image className="w-5 h-5 text-blue-500 shrink-0" /> : <FileText className="w-5 h-5 text-amber-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 truncate group-hover:text-blue-600">{f.original_name}</p>
                          <p className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</p>
                        </div>
                        <Download className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Durum Akis Butonlari */}
            {isAdmin && (
              <div className="mt-4 pt-3 border-t flex flex-wrap gap-2" data-testid="disc-flow-actions">
                {detail.status === 'acik' && (
                  <Button size="sm" variant="outline" onClick={() => handleStatusChange(detail.id, 'tartisiliyor')} data-testid="btn-mark-tartisiliyor">
                    <MessageSquare className="w-4 h-4 mr-1" /> Tartismaya Ac
                  </Button>
                )}
                {detail.status === 'tartisiliyor' && (
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={openVoteDialog} data-testid="btn-start-vote">
                    <Vote className="w-4 h-4 mr-1" /> Oylamaya Gonder
                  </Button>
                )}
                {detail.status === 'oylamada' && voteActive && isAdmin && (
                  <Button size="sm" variant="outline" className="text-amber-600 border-amber-300" onClick={() => handleCloseVote(linkedVote.id)} data-testid="btn-close-vote">
                    <XCircle className="w-4 h-4 mr-1" /> Oylamayi Kapat
                  </Button>
                )}
                {detail.status === 'oylamada' && voteClosed && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openDecisionDialog} data-testid="btn-finalize">
                    <BookOpen className="w-4 h-4 mr-1" /> Karara Bagla
                  </Button>
                )}
                {detail.status === 'oylamada' && voteActive && (
                  <span className="text-xs text-amber-600 flex items-center gap-1 ml-2"><Clock className="w-3.5 h-3.5" /> Oylama devam ediyor, oylama kapandiktan sonra karara baglayabilirsiniz</span>
                )}
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 ml-auto" data-testid="btn-delete-discussion"
                  onClick={async () => {
                    if (!window.confirm('Bu tartismayi silmek istediginize emin misiniz? Tum yorumlar da silinecektir.')) return;
                    try { await wgEngageAPI.deleteDiscussion(organId, detail.id); toast.success('Tartisma silindi'); setSelected(null); setDetail(null); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || 'Tartisma silinemedi'); }
                  }}>
                  <Trash2 className="w-4 h-4 mr-1" /> Sil
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bagli Oylama Karti */}
        {linkedVote && (
          <Card className={`shadow-sm border ${voteActive ? 'border-violet-200 bg-violet-50/30' : 'border-slate-200'}`} data-testid="linked-vote-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Vote className="w-4 h-4 text-violet-500" /> Oylama: {linkedVote.title}
                </h4>
                <Badge className={voteActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                  {voteActive ? 'Aktif' : 'Kapandi'}
                </Badge>
              </div>
              {linkedVote.description && <p className="text-xs text-slate-500 mb-3">{linkedVote.description}</p>}
              <div className="space-y-2">
                {(linkedVote.options || []).map(opt => {
                  const count = (linkedVote.results || {})[opt] || 0;
                  const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                  const isUserVote = linkedVote.user_vote_option === opt;
                  const optLabel = opt === 'evet' ? 'Evet' : opt === 'hayir' ? 'Hayir' : opt === 'cekimser' ? 'Cekimser' : opt;
                  const barColor = opt === 'evet' ? 'bg-emerald-500' : opt === 'hayir' ? 'bg-red-500' : opt === 'cekimser' ? 'bg-slate-400' : 'bg-indigo-500';
                  return (
                    <div key={opt}
                      className={`flex items-center justify-between rounded-lg border p-2.5 transition-all ${isUserVote ? 'border-blue-400 bg-blue-50' : 'border-slate-200'} ${voteActive && !linkedVote.user_voted ? 'cursor-pointer hover:border-blue-300' : ''}`}
                      onClick={() => voteActive && !linkedVote.user_voted && handleCastVote(linkedVote.id, opt)}
                      data-testid={`linked-vote-opt-${opt}`}>
                      <span className="text-sm font-medium text-slate-800">{optLabel}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-16 text-right">{count} oy ({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-2">{totalVotes} toplam oy</p>
              {linkedVote.user_voted && <p className="text-xs text-blue-600 mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Oy kullandiniz</p>}
            </CardContent>
          </Card>
        )}

        {/* Bagli Karar Karti */}
        {linkedDec && (
          <Card className="shadow-sm border border-emerald-200 bg-emerald-50/30" data-testid="linked-decision-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-emerald-500" /> Karar: {linkedDec.title}
                </h4>
                <Badge className={
                  linkedDec.outcome === 'kabul' ? 'bg-emerald-100 text-emerald-700 border-emerald-300 border' :
                  linkedDec.outcome === 'reddedildi' ? 'bg-red-100 text-red-700 border-red-300 border' :
                  'bg-amber-100 text-amber-700 border-amber-300 border'
                }>
                  {linkedDec.outcome === 'kabul' ? 'Kabul Edildi' : linkedDec.outcome === 'reddedildi' ? 'Reddedildi' : 'Ertelendi'}
                </Badge>
              </div>
              {linkedDec.description && <p className="text-sm text-slate-600">{linkedDec.description}</p>}
              {linkedDec.impact && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                  <p className="text-xs font-medium text-blue-700">Etki: {linkedDec.impact}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Yorumlar */}
        <h4 className="text-sm font-semibold text-slate-600 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Yorumlar ({detail.comments?.length || 0})</h4>
        <div className="space-y-3">
          {(detail.comments || []).map(c => {
            const initials = (c.created_by_name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
            const avatarUrl = c.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${c.avatar_url}` : null;
            return (
              <div key={c.id} className="flex gap-3 items-start">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-slate-100 flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-white flex items-center justify-center text-xs font-semibold border-2 border-slate-100 flex-shrink-0">{initials}</div>
                )}
                <Card className="shadow-sm flex-1">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-700">{c.created_by_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-400">{formatRelativeTime(c.created_at)}</span>
                        {isAdmin && <button className="p-0.5 rounded text-slate-300 hover:text-red-500 transition-colors" title="Yorumu sil"
                          onClick={async () => {
                            if (!window.confirm('Bu yorumu silmek istediginize emin misiniz?')) return;
                            try { await wgEngageAPI.deleteComment(organId, detail.id, c.id); toast.success('Yorum silindi'); loadDetail(detail.id); }
                            catch (e) { toast.error(e.response?.data?.detail || 'Yorum silinemedi'); }
                          }}><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    </div>
                    <p className="text-sm text-slate-800 leading-relaxed">{c.content}</p>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
        {detail.status !== 'karara_baglandi' && (
          <div className="flex gap-2">
            <Textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Yorum yazin..." rows={2} className="flex-1" data-testid="comment-input" />
            <Button onClick={handleComment} disabled={commenting || !commentText.trim()} className="self-end" data-testid="send-comment-btn">
              {commenting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}

        {/* OYLAMA BASLAT DIALOG */}
        <Dialog open={showVoteDialog} onOpenChange={setShowVoteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Oylamaya Gonder</DialogTitle>
              <DialogDescription>Bu tartisma icin bir oylama olusturun. Oylama baslatildiktan sonra tartisma "Oylamada" durumuna gececek.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Oylama Basligi *</Label><Input value={voteForm.title} onChange={e => setVoteForm(f => ({ ...f, title: e.target.value }))} placeholder="Neyi oyluyoruz?" data-testid="start-vote-title" /></div>
              <div><Label>Aciklama (Tartisma Ozeti)</Label><Textarea value={voteForm.description} onChange={e => setVoteForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Kisa ozet" data-testid="start-vote-desc" /></div>
              <div><Label>Oylama Turu</Label>
                <Select value={voteForm.vote_type} onValueChange={v => setVoteForm(f => ({ ...f, vote_type: v }))}>
                  <SelectTrigger data-testid="start-vote-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="evet_hayir">Evet / Hayir / Cekimser</SelectItem>
                    <SelectItem value="coktan_secmeli">Coktan Secmeli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {voteForm.vote_type === 'coktan_secmeli' && (
                <div><Label>Secenekler (virgul ile ayirin)</Label><Input value={voteForm.options} onChange={e => setVoteForm(f => ({ ...f, options: e.target.value }))} placeholder="Secenek 1, Secenek 2, Secenek 3" data-testid="start-vote-options" /></div>
              )}
              <div><Label>Oylama Suresi (Bitis Tarihi - Opsiyonel)</Label><Input type="date" value={voteForm.deadline} onChange={e => setVoteForm(f => ({ ...f, deadline: e.target.value }))} data-testid="start-vote-deadline" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVoteDialog(false)}>Iptal</Button>
              <Button onClick={handleStartVote} disabled={startingVote} className="bg-violet-600 hover:bg-violet-700" data-testid="submit-start-vote">
                {startingVote ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Vote className="w-4 h-4 mr-1" />} Oylamayi Baslat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* KARARA BAGLA DIALOG */}
        <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Karara Bagla</DialogTitle>
              <DialogDescription>Oylama sonucuna gore karari kaydedin. Tartisma "Karara Baglandi" durumuna gececek.</DialogDescription>
            </DialogHeader>
            {/* Oylama ozeti */}
            {linkedVote && (
              <div className="bg-slate-50 rounded-lg p-3 mb-1">
                <p className="text-xs font-semibold text-slate-600 mb-1.5">Oylama Sonucu:</p>
                <div className="flex gap-3 flex-wrap">
                  {(linkedVote.options || []).map(opt => {
                    const count = (linkedVote.results || {})[opt] || 0;
                    const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                    const optLabel = opt === 'evet' ? 'Evet' : opt === 'hayir' ? 'Hayir' : opt === 'cekimser' ? 'Cekimser' : opt;
                    return <Badge key={opt} variant="outline" className="text-xs">{optLabel}: {count} ({pct}%)</Badge>;
                  })}
                </div>
              </div>
            )}
            <div className="space-y-3 py-1">
              <div><Label>Karar Ozeti *</Label><Input value={decForm.title} onChange={e => setDecForm(f => ({ ...f, title: e.target.value }))} placeholder="Karar ozeti (2-3 cumle)" data-testid="finalize-title" /></div>
              <div><Label>Detay</Label><Textarea value={decForm.description} onChange={e => setDecForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Karar detayi" data-testid="finalize-desc" /></div>
              <div><Label>Sonuc</Label>
                <Select value={decForm.outcome} onValueChange={v => setDecForm(f => ({ ...f, outcome: v }))}>
                  <SelectTrigger data-testid="finalize-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kabul">Kabul Edildi</SelectItem>
                    <SelectItem value="reddedildi">Reddedildi</SelectItem>
                    <SelectItem value="ertelendi">Ertelendi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Kararin Etkisi</Label><Textarea value={decForm.impact} onChange={e => setDecForm(f => ({ ...f, impact: e.target.value }))} rows={2} placeholder="Bu kararla ne degisecek?" data-testid="finalize-impact" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>Iptal</Button>
              <Button onClick={handleFinalize} disabled={finalizing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-finalize">
                {finalizing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BookOpen className="w-4 h-4 mr-1" />} Karara Bagla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="discussions-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-600">Tartisma Panosu</h3>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="new-discussion-btn"><Plus className="w-4 h-4 mr-1" /> Yeni Gundem</Button>
      </div>
      {(() => {
        const filtered = discussions.filter(d => {
          if (searchTerm && !d.title?.toLowerCase().includes(searchTerm.toLowerCase()) && !d.content?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
          if (statusFilter !== 'all' && d.status !== statusFilter) return false;
          return true;
        });
        return filtered.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-slate-400"><MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />{discussions.length === 0 ? 'Henuz tartisma baslatilmamis' : 'Filtreyle eslesen tartisma bulunamadi'}</CardContent></Card>
        ) : (
        <div className="space-y-2">
          {filtered.map(d => {
            const st = DISC_STATUS[d.status] || DISC_STATUS.acik;
            return (
              <Card key={d.id} className="shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => loadDetail(d.id)} data-testid={`disc-${d.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-900 truncate">{d.title}</h4>
                      <Badge className={`${st.cls} border text-xs shrink-0`}>{st.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">
                      {d.created_by_name} - {formatRelativeTime(d.created_at)} - {d.comment_count || 0} yorum
                      {d.files && d.files.length > 0 && <span className="ml-1"><Paperclip className="w-3 h-3 inline" /> {d.files.length}</span>}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
                </CardContent>
              </Card>
            );
          })}
        </div>
        );
      })()}
      <Dialog open={showForm} onOpenChange={(v) => { setShowForm(v); if (!v) setSelectedFiles([]); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Yeni Gundem Maddesi</DialogTitle><DialogDescription>Grubunuzda tartismak istediginiz konuyu paylasın</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            <div><Label>Baslik *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Gundem basligi" data-testid="disc-title-input" /></div>
            <div><Label>Icerik *</Label><Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={4} placeholder="Detayli aciklama..." data-testid="disc-content-input" /></div>
            {/* Dosya Ekleme */}
            <div>
              <Label className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Dosya Ekle (Opsiyonel)</Label>
              <p className="text-xs text-slate-400 mb-2">Max {DISC_MAX_FILES} dosya, toplam 25MB. (jpg, png, pdf, word, excel)</p>
              {selectedFiles.length < DISC_MAX_FILES && (
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg p-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-colors" data-testid="disc-file-upload-area">
                  <Plus className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Dosya sec</span>
                  <input type="file" className="hidden" multiple accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileSelect} data-testid="disc-file-input" />
                </label>
              )}
              {selectedFiles.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  {selectedFiles.map((f, idx) => {
                    const isImg = /\.(jpg|jpeg|png)$/i.test(f.name);
                    return (
                      <div key={f.name || `file-${idx}`} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100" data-testid={`disc-file-${idx}`}>
                        {isImg ? <Image className="w-4 h-4 text-blue-500 shrink-0" /> : <FileText className="w-4 h-4 text-amber-500 shrink-0" />}
                        <span className="text-sm text-slate-700 truncate flex-1">{f.name}</span>
                        <span className="text-xs text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                        <button type="button" onClick={() => removeFile(idx)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                  <p className="text-xs text-slate-400">{selectedFiles.length}/{DISC_MAX_FILES} dosya - {(selectedFiles.reduce((s, f) => s + f.size, 0) / (1024*1024)).toFixed(1)} MB / 25 MB</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setSelectedFiles([]); }}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-disc-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Olustur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== FAZ 1: HIZLI OYLAMA =====================
export const VotesTab = ({ organId, isAdmin, searchTerm = '', statusFilter = 'all' }) => {
  const { user } = useAuth();
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', vote_type: 'evet_hayir', options: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { load(); }, [organId]);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await wgEngageAPI.getVotes(organId); setVotes(r.data); }
    catch { toast.error('Oylamalar yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Baslik zorunlu'); return; }
    setCreating(true);
    try {
      const payload = { title: form.title, description: form.description, vote_type: form.vote_type };
      if (form.vote_type === 'coktan_secmeli') payload.options = form.options.split(',').map(s => s.trim()).filter(Boolean);
      await wgEngageAPI.createVote(organId, payload);
      toast.success('Oylama olusturuldu');
      setShowForm(false); setForm({ title: '', description: '', vote_type: 'evet_hayir', options: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    finally { setCreating(false); }
  };

  const handleCastVote = async (voteId, option) => {
    try { await wgEngageAPI.castVote(organId, voteId, { option }); toast.success('Oyunuz kaydedildi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Oy kullanilamadi'); }
  };

  const handleCloseVote = async (voteId) => {
    try { await wgEngageAPI.closeVote(organId, voteId); toast.success('Oylama kapatildi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kapatilamadi'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-4" data-testid="votes-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-600">Oylamalar</h3>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="new-vote-btn"><Plus className="w-4 h-4 mr-1" /> Yeni Oylama</Button>
      </div>
      {(() => {
        const filtered = votes.filter(v => {
          if (searchTerm && !v.title?.toLowerCase().includes(searchTerm.toLowerCase()) && !v.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
          if (statusFilter !== 'all' && v.status !== statusFilter) return false;
          return true;
        });
        return filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400"><Vote className="w-10 h-10 mx-auto mb-2 text-slate-300" />{votes.length === 0 ? 'Henuz oylama yok' : 'Filtreyle eslesen oylama bulunamadi'}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(v => {
            const isActive = v.status === 'aktif';
            const totalVotes = v.total_votes || 0;
            return (
              <Card key={v.id} className="shadow-sm" data-testid={`vote-${v.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-slate-900">{v.title}</h4>
                      {v.description && <p className="text-sm text-slate-500 mt-0.5">{v.description}</p>}
                      <p className="text-xs text-slate-400 mt-1">{v.created_by_name} - {formatRelativeTime(v.created_at)} - {totalVotes} oy</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{isActive ? 'Aktif' : 'Kapandi'}</Badge>
                      {isActive && isAdmin && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleCloseVote(v.id)}>Kapat</Button>}
                      {isAdmin && <button data-testid={`delete-vote-${v.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Oylamayi sil"
                        onClick={async () => {
                          if (!window.confirm('Bu oylamayi silmek istediginize emin misiniz?')) return;
                          try { await wgEngageAPI.deleteVote(organId, v.id); toast.success('Oylama silindi'); loadVotes(); }
                          catch (e) { toast.error(e.response?.data?.detail || 'Oylama silinemedi'); }
                        }}><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                  <div className="space-y-1.5 mt-3">
                    {(v.options || []).map(opt => {
                      const count = (v.results || {})[opt] || 0;
                      const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                      const isUserVote = v.user_vote_option === opt;
                      return (
                        <div key={opt} className="relative">
                          <div className={`flex items-center justify-between rounded-lg border p-2.5 transition-all ${isUserVote ? 'border-blue-400 bg-blue-50' : 'border-slate-200'} ${isActive && !v.user_voted ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/50' : ''}`}
                            onClick={() => isActive && !v.user_voted && handleCastVote(v.id, opt)}
                            data-testid={`vote-option-${v.id}-${opt}`}>
                            <span className="text-sm font-medium text-slate-800 capitalize z-10 relative">{opt === 'evet' ? 'Evet' : opt === 'hayir' ? 'Hayir' : opt === 'cekimser' ? 'Cekimser' : opt}</span>
                            <span className="text-xs text-slate-500 z-10 relative">{count} oy ({pct}%)</span>
                            <div className="absolute left-0 top-0 h-full rounded-lg bg-blue-100/60 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {v.user_voted && <p className="text-xs text-blue-600 mt-2 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Oy kullandiniz</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
        );
      })()}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Oylama</DialogTitle><DialogDescription>Grup uyelerinin oy kullanacagi bir oylama olusturun</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Baslik *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Oylama basligi" data-testid="vote-title-input" /></div>
            <div><Label>Aciklama</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Oylama aciklamasi" /></div>
            <div><Label>Tur</Label>
              <Select value={form.vote_type} onValueChange={v => setForm(f => ({ ...f, vote_type: v }))}>
                <SelectTrigger data-testid="vote-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="evet_hayir">Evet / Hayir / Cekimser</SelectItem>
                  <SelectItem value="coktan_secmeli">Coktan Secmeli</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.vote_type === 'coktan_secmeli' && (
              <div><Label>Secenekler (virgul ile ayirin)</Label><Input value={form.options} onChange={e => setForm(f => ({ ...f, options: e.target.value }))} placeholder="Secenek 1, Secenek 2, Secenek 3" data-testid="vote-options-input" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-vote-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Vote className="w-4 h-4 mr-1" />} Olustur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== FAZ 2: KARAR DEFTERI (Zengin Goruntuleme) =====================

const OUTCOME_MAP = {
  kabul: { label: 'Kabul Edildi', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle, color: 'text-emerald-600' },
  reddedildi: { label: 'Reddedildi', cls: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, color: 'text-red-600' },
  ertelendi: { label: 'Ertelendi', cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock, color: 'text-amber-600' },
};

const VOTE_OPTION_COLORS = {
  evet: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  hayir: { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  cekimser: { bar: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-50' },
};

const TIMELINE_ICONS = {
  tartisma: MessageSquare,
  oylama: Vote,
  karar: BookOpen,
};

// Karar Detay Gorunumu
const DecisionDetailView = ({ organId, decisionId, onBack, isAdmin, onStatusChange, onGoToTasks }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDetail(); }, [decisionId]);
  const loadDetail = useCallback(async () => {
    setLoading(true);
    try { const r = await wgEngageAPI.getDecision(organId, decisionId); setDetail(r.data); }
    catch { toast.error('Karar detayi yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  if (!detail) return <div className="text-center py-8 text-slate-400">Karar bulunamadi</div>;

  const outcome = OUTCOME_MAP[detail.outcome] || OUTCOME_MAP.kabul;
  const OutcomeIcon = outcome.icon;
  const vote = detail.source_vote;
  const disc = detail.source_discussion;
  const totalVotes = vote?.total_votes || 0;

  return (
    <div className="space-y-5" data-testid="decision-detail-view">
      {/* Geri Butonu */}
      <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-500 hover:text-slate-700" data-testid="decision-back-btn">
        <XCircle className="w-4 h-4 mr-1" /> Kararlara Don
      </Button>

      {/* Karar Baslik Karti */}
      <Card className="shadow-md border-l-4" style={{ borderLeftColor: detail.outcome === 'kabul' ? '#10b981' : detail.outcome === 'reddedildi' ? '#ef4444' : '#f59e0b' }}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${detail.outcome === 'kabul' ? 'bg-emerald-100' : detail.outcome === 'reddedildi' ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <OutcomeIcon className={`w-5 h-5 ${outcome.color}`} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900" data-testid="decision-detail-title">{detail.title}</h2>
                  <Badge className={`${outcome.cls} border text-xs mt-0.5`} data-testid="decision-outcome-badge">{outcome.label}</Badge>
                </div>
              </div>
              {detail.description && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{detail.description}</p>}
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                <span data-testid="decision-author">{detail.created_by_name}</span>
                <span>{formatRelativeTime(detail.created_at)}</span>
                {detail.contributor_count > 0 && (
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {detail.contributor_count} katilimci</span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Select value={detail.status} onValueChange={(v) => { onStatusChange(detail.id, v); loadDetail(); }}>
                  <SelectTrigger className="h-8 text-xs w-[140px] shrink-0" data-testid="decision-detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DEC_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button data-testid="btn-delete-decision" className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Karari sil"
                  onClick={async () => {
                    if (!window.confirm('Bu karari silmek istediginize emin misiniz?')) return;
                    try { await wgEngageAPI.deleteDecision(organId, detail.id); toast.success('Karar silindi'); onBack(); }
                    catch (e) { toast.error(e.response?.data?.detail || 'Karar silinemedi'); }
                  }}><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Zaman Cizelgesi */}
      {detail.timeline && detail.timeline.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" /> Surecin Zaman Cizelgesi
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4" data-testid="decision-timeline">
            <div className="relative">
              {detail.timeline.map((ev, idx) => {
                const TlIcon = TIMELINE_ICONS[ev.type] || BookOpen;
                const isLast = idx === detail.timeline.length - 1;
                const iconBg = ev.type === 'tartisma' ? 'bg-blue-100 text-blue-600'
                  : ev.type === 'oylama' ? 'bg-violet-100 text-violet-600'
                  : 'bg-emerald-100 text-emerald-600';
                return (
                  <div key={ev.type || `tl-${idx}`} className="flex gap-3 relative" data-testid={`timeline-step-${ev.type}`}>
                    {/* Dikey Cizgi */}
                    {!isLast && <div className="absolute left-[15px] top-[32px] w-0.5 h-[calc(100%-8px)] bg-slate-200" />}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${iconBg}`}>
                      <TlIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 pb-5">
                      <p className="font-semibold text-sm text-slate-800">{ev.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{ev.detail}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">{ev.sub}</span>
                        {ev.date && <span className="text-xs text-slate-300">- {new Date(ev.date).toLocaleDateString('tr-TR')}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Oylama Sonuclari */}
      {vote && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-500" /> Oylama Sonuclari
              <span className="text-xs font-normal text-slate-400 ml-auto">{totalVotes} toplam oy</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4" data-testid="decision-vote-results">
            <p className="text-xs text-slate-500 mb-3">{vote.title}</p>
            <div className="space-y-2.5">
              {(vote.options || []).map(opt => {
                const count = (vote.results || {})[opt] || 0;
                const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                const colors = VOTE_OPTION_COLORS[opt] || { bar: 'bg-indigo-500', text: 'text-indigo-700', bg: 'bg-indigo-50' };
                const optLabel = opt === 'evet' ? 'Evet' : opt === 'hayir' ? 'Hayir' : opt === 'cekimser' ? 'Cekimser' : opt;
                return (
                  <div key={opt} data-testid={`vote-result-${opt}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${colors.text}`}>{optLabel}</span>
                      <span className="text-sm font-bold text-slate-700">{count} oy ({pct}%)</span>
                    </div>
                    <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${colors.bar}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kararin Etkisi */}
      {detail.impact && (
        <Card className="shadow-sm border border-blue-100 bg-blue-50/30">
          <CardContent className="p-5" data-testid="decision-impact">
            <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2 mb-2">
              <ArrowRight className="w-4 h-4" /> Kararin Etkisi
            </h4>
            <p className="text-sm text-blue-700 leading-relaxed">{detail.impact}</p>
          </CardContent>
        </Card>
      )}

      {/* Kaynak Tartisma Ozeti */}
      {disc && (
        <Card className="shadow-sm">
          <CardContent className="p-5" data-testid="decision-source-discussion">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-blue-500" /> Kaynak Tartisma
            </h4>
            <p className="font-medium text-slate-800 text-sm">{disc.title}</p>
            <p className="text-xs text-slate-500 mt-1 line-clamp-3">{disc.content}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span>{disc.created_by_name}</span>
              <span>{disc.comment_count} yorum</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gorevler / Aksiyon Maddeleri */}
      {detail.tasks && detail.tasks.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <ListTodo className="w-4 h-4 text-teal-500" /> Aksiyon Maddeleri
              <Badge variant="outline" className="text-xs ml-auto">{detail.done_task_count}/{detail.task_count} tamamlandi</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4" data-testid="decision-action-items">
            <div className="space-y-2">
              {detail.tasks.map(t => {
                const tst = TASK_STATUS[t.status] || TASK_STATUS.yapilacak;
                return (
                  <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 bg-white" data-testid={`decision-task-${t.id}`}>
                    <div className={`w-2 h-2 rounded-full shrink-0 ${t.status === 'tamamlandi' ? 'bg-emerald-500' : t.status === 'yapiliyor' ? 'bg-blue-500' : 'bg-slate-300'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${t.status === 'tamamlandi' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{t.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {t.assigned_name && <span className="text-xs text-slate-400">{t.assigned_name}</span>}
                        {t.due_date && <span className="text-xs text-slate-400">{t.due_date}</span>}
                      </div>
                    </div>
                    <Badge className={`${tst.cls} text-xs shrink-0`}>{tst.label}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alt Butonlar */}
      <div className="flex gap-3 pt-2">
        {disc && (
          <Button variant="outline" size="sm" onClick={onBack} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="go-to-discussion-btn">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Tartismalara Don
          </Button>
        )}
        {detail.tasks && detail.tasks.length > 0 && onGoToTasks && (
          <Button variant="outline" size="sm" onClick={onGoToTasks} className="text-teal-600 border-teal-200 hover:bg-teal-50" data-testid="go-to-tasks-btn">
            <ListTodo className="w-4 h-4 mr-1.5" /> Gorevleri Gor
          </Button>
        )}
      </div>
    </div>
  );
};

export const DecisionsTab = ({ organId, isAdmin, onGoToTasks, searchTerm = '', statusFilter = 'all' }) => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', outcome: 'kabul', impact: '', source_discussion_id: '', source_vote_id: '' });
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [votes, setVotes] = useState([]);

  useEffect(() => { load(); }, [organId]);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await wgEngageAPI.getDecisions(organId); setDecisions(r.data); }
    catch { toast.error('Kararlar yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const loadFormData = async () => {
    try {
      const [dRes, vRes] = await Promise.all([wgEngageAPI.getDiscussions(organId), wgEngageAPI.getVotes(organId)]);
      setDiscussions(dRes.data || []);
      setVotes(vRes.data || []);
    } catch { /* silent */ }
  };

  const openCreateForm = () => {
    loadFormData();
    setForm({ title: '', description: '', outcome: 'kabul', impact: '', source_discussion_id: '', source_vote_id: '' });
    setShowForm(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Baslik zorunlu'); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.source_discussion_id) delete payload.source_discussion_id;
      if (!payload.source_vote_id) delete payload.source_vote_id;
      if (!payload.impact) delete payload.impact;
      await wgEngageAPI.createDecision(organId, payload);
      toast.success('Karar kaydedildi');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    finally { setCreating(false); }
  };

  const handleStatusChange = async (decId, status) => {
    try { await wgEngageAPI.updateDecisionStatus(organId, decId, status); toast.success('Durum guncellendi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Guncellenemedi'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  // Karar Detay Gorunumu
  if (selectedId) {
    return (
      <DecisionDetailView
        organId={organId}
        decisionId={selectedId}
        onBack={() => setSelectedId(null)}
        isAdmin={isAdmin}
        onStatusChange={handleStatusChange}
        onGoToTasks={onGoToTasks}
      />
    );
  }

  // Liste Gorunumu
  return (
    <div className="space-y-4" data-testid="decisions-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-600">Karar Defteri</h3>
        {isAdmin && <Button size="sm" onClick={openCreateForm} data-testid="new-decision-btn"><Plus className="w-4 h-4 mr-1" /> Karar Ekle</Button>}
      </div>
      {(() => {
        const filtered = decisions.filter(d => {
          if (searchTerm && !d.title?.toLowerCase().includes(searchTerm.toLowerCase()) && !d.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
          if (statusFilter !== 'all' && d.status !== statusFilter && d.outcome !== statusFilter) return false;
          return true;
        });
        return filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400"><BookOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" />{decisions.length === 0 ? 'Henuz karar alinmamis' : 'Filtreyle eslesen karar bulunamadi'}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(d => {
            const st = DEC_STATUS[d.status] || DEC_STATUS.bekliyor;
            const oc = OUTCOME_MAP[d.outcome] || OUTCOME_MAP.kabul;
            const OcIcon = oc.icon;
            return (
              <Card key={d.id} className="shadow-sm hover:shadow-md transition-all cursor-pointer group" onClick={() => setSelectedId(d.id)} data-testid={`decision-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${d.outcome === 'kabul' ? 'bg-emerald-100' : d.outcome === 'reddedildi' ? 'bg-red-100' : 'bg-amber-100'}`}>
                      <OcIcon className={`w-4 h-4 ${oc.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900 truncate">{d.title}</h4>
                        <Badge className={`${oc.cls} border text-xs shrink-0`}>{oc.label}</Badge>
                        <Badge className={`${st.cls} text-xs shrink-0`}>{st.label}</Badge>
                      </div>
                      {d.description && <p className="text-sm text-slate-500 line-clamp-1 mb-1">{d.description}</p>}
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span>{d.created_by_name} - {formatRelativeTime(d.created_at)}</span>
                        {d.source_discussion_title && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {d.source_discussion_title}</span>}
                        {d.task_count > 0 && <span className="flex items-center gap-1"><ListTodo className="w-3 h-3" /> {d.done_task_count}/{d.task_count} gorev</span>}
                      </div>
                      {d.vote_summary?.results && (
                        <div className="flex gap-3 mt-2">
                          {Object.entries(d.vote_summary.results).map(([k, v]) => {
                            const total = d.vote_summary.total_votes || 1;
                            const pct = Math.round((v / total) * 100);
                            const optLabel = k === 'evet' ? 'Evet' : k === 'hayir' ? 'Hayir' : k === 'cekimser' ? 'Cekimser' : k;
                            return (
                              <div key={k} className="flex items-center gap-1.5">
                                <div className={`w-1.5 h-1.5 rounded-full ${k === 'evet' ? 'bg-emerald-500' : k === 'hayir' ? 'bg-red-500' : 'bg-slate-400'}`} />
                                <span className="text-xs text-slate-500">{optLabel}: {pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 mt-1 transition-colors" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        );
      })()}

      {/* Zenginlestirilmis Karar Olusturma Formu */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Yeni Karar Kaydi</DialogTitle><DialogDescription>Alinmis karari tum detaylariyla karar defterine kaydedin</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            <div><Label>Karar Basligi *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Karar ozeti" data-testid="decision-title-input" /></div>
            <div><Label>Detay</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Karar detayi" data-testid="decision-desc-input" /></div>
            <div><Label>Sonuc</Label>
              <Select value={form.outcome} onValueChange={v => setForm(f => ({ ...f, outcome: v }))}>
                <SelectTrigger data-testid="decision-outcome-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kabul">Kabul Edildi</SelectItem>
                  <SelectItem value="reddedildi">Reddedildi</SelectItem>
                  <SelectItem value="ertelendi">Ertelendi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Kararin Etkisi</Label><Textarea value={form.impact} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))} rows={2} placeholder="Bu kararla ne degisecek?" data-testid="decision-impact-input" /></div>
            {discussions.length > 0 && (
              <div><Label>Kaynak Tartisma (Opsiyonel)</Label>
                <Select value={form.source_discussion_id || "none"} onValueChange={v => setForm(f => ({ ...f, source_discussion_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="decision-source-disc-select"><SelectValue placeholder="Bir tartisma secin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Secilmedi</SelectItem>
                    {discussions.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {votes.length > 0 && (
              <div><Label>Kaynak Oylama (Opsiyonel)</Label>
                <Select value={form.source_vote_id || "none"} onValueChange={v => setForm(f => ({ ...f, source_vote_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="decision-source-vote-select"><SelectValue placeholder="Bir oylama secin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Secilmedi</SelectItem>
                    {votes.map(v => <SelectItem key={v.id} value={v.id}>{v.title} ({v.total_votes} oy)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-decision-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BookOpen className="w-4 h-4 mr-1" />} Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== FAZ 2: GOREV TAHTASI =====================
export const TasksTab = ({ organId, isAdmin, members, searchTerm = '', statusFilter = 'all' }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', due_date: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { load(); }, [organId]);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await wgEngageAPI.getTasks(organId); setTasks(r.data); }
    catch { toast.error('Gorevler yuklenemedi'); }
    finally { setLoading(false); }
  }, [organId]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Baslik zorunlu'); return; }
    setCreating(true);
    try { await wgEngageAPI.createTask(organId, form); toast.success('Gorev olusturuldu'); setShowForm(false); setForm({ title: '', description: '', assigned_to: '', due_date: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    finally { setCreating(false); }
  };

  const handleStatusChange = async (taskId, status) => {
    try { await wgEngageAPI.updateTask(organId, taskId, { status }); toast.success('Durum guncellendi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Guncellenemedi'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  const filteredTasks = tasks.filter(t => {
    if (searchTerm && !t.title?.toLowerCase().includes(searchTerm.toLowerCase()) && !t.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    return true;
  });
  const grouped = { yapilacak: filteredTasks.filter(t => t.status === 'yapilacak'), yapiliyor: filteredTasks.filter(t => t.status === 'yapiliyor'), tamamlandi: filteredTasks.filter(t => t.status === 'tamamlandi') };

  return (
    <div className="space-y-4" data-testid="tasks-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-600">Gorev Tahtasi</h3>
        {isAdmin && <Button size="sm" onClick={() => setShowForm(true)} data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" /> Yeni Gorev</Button>}
      </div>
      {tasks.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400"><ListTodo className="w-10 h-10 mx-auto mb-2 text-slate-300" />Henuz gorev yok</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(grouped).map(([status, items]) => {
            const st = TASK_STATUS[status];
            return (
              <div key={status}>
                <div className={`flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg ${st.cls}`}>
                  <span className="text-sm font-semibold">{st.label}</span>
                  <Badge variant="outline" className="text-xs h-5">{items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {items.map(t => (
                    <Card key={t.id} className="shadow-sm" data-testid={`task-${t.id}`}>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm text-slate-900 mb-1">{t.title}</h4>
                        {t.description && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{t.description}</p>}
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-slate-400">
                            {t.assigned_name && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {t.assigned_name}</span>}
                            {t.due_date && <span className="flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {t.due_date}</span>}
                          </div>
                          <Select value={t.status} onValueChange={(v) => handleStatusChange(t.id, v)}>
                            <SelectTrigger className="h-6 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(TASK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Gorev</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Baslik *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Gorev basligi" data-testid="task-title-input" /></div>
            <div><Label>Aciklama</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Gorev detayi" /></div>
            {members && members.length > 0 && (
              <div><Label>Sorumlu</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Kisi secin" /></SelectTrigger>
                  <SelectContent>{members.map(m => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.user_name || m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Bitis Tarihi</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-task-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ListTodo className="w-4 h-4 mr-1" />} Olustur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== FAZ 3: ROL + KATKI GORUNURLUGU =====================
export const ContributionsTab = ({ organId, isAdmin, members }) => {
  const [contributions, setContributions] = useState([]);
  const [memberRoles, setMemberRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [organId]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, rRes] = await Promise.all([wgEngageAPI.getContributions(organId), wgEngageAPI.getMemberRoles(organId)]);
      setContributions(cRes.data);
      setMemberRoles(rRes.data);
    } catch { toast.error('Katki verileri yuklenemedi'); }
    finally { setLoading(false); }
  }, [organId]);

  const handleRoleChange = async (userId, role) => {
    try { await wgEngageAPI.setMemberRole(organId, userId, { role }); toast.success('Rol atandi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Atanamadi'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  const roleMap = {};
  memberRoles.forEach(r => { roleMap[r.user_id] = r.role; });

  const ACTION_LABELS = { tartisma: 'Tartisma', yorum: 'Yorum', oylama: 'Oylama', gorev: 'Gorev', karar: 'Karar', nabiz: 'Nabiz', oylama_olustur: 'Oylama Olusturma' };

  return (
    <div className="space-y-4" data-testid="contributions-tab">
      <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2"><Activity className="w-4 h-4" /> Uye Katkilari ve Roller</h3>
      {contributions.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400"><Activity className="w-10 h-10 mx-auto mb-2 text-slate-300" />Henuz katki verisi yok</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {contributions.map((c, idx) => {
            const role = roleMap[c.user_id] || c.role || 'uye';
            const ri = ROLE_MAP[role] || ROLE_MAP.uye;
            return (
              <Card key={c.user_id} className="shadow-sm" data-testid={`contrib-${c.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{c.user_name}</span>
                          <Badge className={`${ri.cls} text-xs`}>{ri.label}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {Object.entries(c.details || {}).map(([action, count]) => (
                            <span key={action} className="text-xs text-slate-500">{ACTION_LABELS[action] || action}: {count}</span>
                          ))}
                          {c.total_contributions === 0 && <span className="text-xs text-slate-400">Henuz katki yok</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600">{c.total_contributions}</p>
                        <p className="text-xs text-slate-400">toplam</p>
                      </div>
                      {isAdmin && (
                        <Select value={role} onValueChange={(v) => handleRoleChange(c.user_id, v)}>
                          <SelectTrigger className="h-7 text-xs w-[120px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(ROLE_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===================== FAZ 4: HAFTALIK NABIZ =====================
export const PulsesTab = ({ organId, isAdmin }) => {
  const [pulses, setPulses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState('');
  const [creating, setCreating] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [answering, setAnswering] = useState(null);
  const [responses, setResponses] = useState(null);
  const [viewingPulseId, setViewingPulseId] = useState(null);

  useEffect(() => { load(); }, [organId]);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await wgEngageAPI.getPulses(organId); setPulses(r.data); }
    catch { toast.error('Nabiz verileri yuklenemedi'); }
    finally { setLoading(false); }
  }, [organId]);

  const handleCreate = async () => {
    if (!question.trim()) { toast.error('Soru zorunlu'); return; }
    setCreating(true);
    try { await wgEngageAPI.createPulse(organId, { question }); toast.success('Nabiz sorusu olusturuldu'); setShowForm(false); setQuestion(''); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    finally { setCreating(false); }
  };

  const handleRespond = async (pulseId) => {
    if (!answerText.trim()) return;
    setAnswering(pulseId);
    try { await wgEngageAPI.respondPulse(organId, pulseId, { answer: answerText }); toast.success('Yanitiniz kaydedildi'); setAnswerText(''); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Yanitlanamadi'); }
    finally { setAnswering(null); }
  };

  const viewResponses = async (pulseId) => {
    if (viewingPulseId === pulseId) { setViewingPulseId(null); setResponses(null); return; }
    try { const r = await wgEngageAPI.getPulseResponses(organId, pulseId); setResponses(r.data); setViewingPulseId(pulseId); }
    catch { toast.error('Yanitlar yuklenemedi'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-4" data-testid="pulses-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2"><HeartPulse className="w-4 h-4" /> Haftalik Nabiz</h3>
        {isAdmin && <Button size="sm" onClick={() => setShowForm(true)} data-testid="new-pulse-btn"><Plus className="w-4 h-4 mr-1" /> Yeni Soru</Button>}
      </div>
      {pulses.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400"><HeartPulse className="w-10 h-10 mx-auto mb-2 text-slate-300" />Henuz nabiz sorusu yok</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {pulses.map(p => (
            <Card key={p.id} className="shadow-sm" data-testid={`pulse-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-slate-900">{p.question}</h4>
                    <p className="text-xs text-slate-400 mt-0.5">{formatRelativeTime(p.created_at)} - {p.response_count} yanit</p>
                  </div>
                  <Badge className={p.status === 'aktif' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{p.status === 'aktif' ? 'Aktif' : 'Kapandi'}</Badge>
                </div>
                {p.status === 'aktif' && !p.user_responded && (
                  <div className="flex gap-2 mt-2">
                    <Input value={answering === p.id ? answerText : ''} onChange={e => { setAnswering(p.id); setAnswerText(e.target.value); }}
                      placeholder="Yanitinizi yazin..." className="flex-1" data-testid={`pulse-answer-${p.id}`}
                      onFocus={() => setAnswering(p.id)} />
                    <Button size="sm" onClick={() => handleRespond(p.id)} disabled={answering === p.id && !answerText.trim()} data-testid={`pulse-submit-${p.id}`}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                {p.user_responded && <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Yanitladiniz</p>}
                {isAdmin && p.response_count > 0 && (
                  <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => viewResponses(p.id)}>
                    <Eye className="w-3 h-3 mr-1" /> {viewingPulseId === p.id ? 'Gizle' : 'Yanitlari Gor'}
                  </Button>
                )}
                {viewingPulseId === p.id && responses && (
                  <div className="mt-2 space-y-1.5 border-t pt-2">
                    {responses.map(r => (
                      <div key={r.id} className="bg-slate-50 rounded-lg p-2">
                        <p className="text-sm text-slate-800">{r.answer}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{r.user_name} - {formatRelativeTime(r.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Nabiz Sorusu</DialogTitle><DialogDescription>Bu haftanin sorusunu olusturun</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Soru *</Label><Textarea value={question} onChange={e => setQuestion(e.target.value)} rows={3} placeholder="Bu hafta grubumuzun onceligi ne olmali?" data-testid="pulse-question-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-pulse-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <HeartPulse className="w-4 h-4 mr-1" />} Olustur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
